import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { resolveSignageSessionOrganizationId } from "../../api/salesOrderApi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import { Modal, Button, Form } from "react-bootstrap";
import { FormikValues } from "formik";
import { Office, Product, Warehouse } from "types";
import DataTable from "components/DataTable";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { StatusBadge } from "components/Productlocationstatus";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import imageCustomSquare from "assets/images/imagecustomrounded.png";
import BinLocationPopUp from "components/BinLocationPopUp";
import { sendTriggerEmails } from "utils/emailService";
import { fetchUserData, fetchCustomerGroupByDomain, fetchOfficeTeamMembers } from "utils/userDataFetcher";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";

interface MyTableMeta {
  noteSelections: Record<string, string>;
  binLocationSelections: Record<string, string>;
  rotationProduct: Record<string, string>;
  warehouseSelections: Record<string, string>;
  supplierActionSelections: Record<string, string>;
  handleNoteChange: (productId: string, value: string) => void;
  handleBinLocationChange: (productId: string, value: string) => void;
  handleRotationalProduct: (productId: string, value: string) => void;
  handleWarehouseChange: (productId: string, value: string) => void;
  handleSupplierActionChange: (productId: string, value: string) => void;
  handleSupplierAction: (productId: string, agentId: string) => void;
  handleUnlink: (productId: string, agentId: string) => void;
  warehouses: Warehouse[];
  zoneSelections: Record<string, string>;
  rockSelections: Record<string, string>;
  handleZoneChange: (productId: string, value: string) => void;
  handleRockChange: (productId: string, value: string) => void;
  warehouseData: Record<string, WarehouseHierarchy>;
  showBinLocationPopup: Record<string, boolean>;
  setShowBinLocationPopup: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  selectedLocations: Record<string, Array<{
    quantity: number;
    warehouse_id: string; warehouse_name: string;
    zone_id: string; zone_name: string;
    rack_id: string; rack_name: string;
    bin_id: string; bin_name: string;
  }>>;
}

interface ExportRow {
  "First Name": string;
  "Last Name": string;
  "Email": string;
  "Phone": string;
  "Brokerage Firm Affiliated": string;
  "Office": string;
  "Address": string;
  "License Status": string;
  "Status": string;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

type Dimensions = {
  length: number;
  height: number;
  width: number;
  weight: number;
};

type Image = {
  url: string;
};

type OfficeProduct = {
  brokerage_firm: string;
  product_id: string;
  office: Office;
  product: Product;
  inventory_type: string;
  created_by: { id: string; name: string };
  owner_node_type?: string;
  owner_node_id?: string;
};

// type Warehouse = {
//   id: string;
//   label: string;
//   status: string;
// };

type SelectionState = {
  [productId: string]: string;
};

type WarehouseHierarchy = {
  zones: { _id: string, label: string }[],
  racks: { _id: string, label: string }[],
  bins: { _id: string, label: string, max_volume?: number, used_volume?: number }[],
  selectedZone?: { _id: string, label: string },
  selectedRack?: { _id: string, label: string },
  selectedBin?: { _id: string, label: string }
};


const columnHelper = createColumnHelper<OfficeProduct>();
const HEX24 = /^[a-f0-9]{24}$/i;
const ABS_URL = /^https?:\/\//i;
const isHexId = (s?: string) => !!(s && HEX24.test(s.trim()));

const SignageIncomingOfficeProducts = () => {
  const [officeProducts, setOfficeProducts] = useState<OfficeProduct[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null); // Track deletion in progress
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const membershipId = getAuthCookie("membershipId");
  const organizationId = getAuthCookie("organizationId");
  const effectiveSignageOrgId = resolveSignageSessionOrganizationId() || organizationId || userId || "";
  const { canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.office_incoming_products
  );
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<OfficeProduct | null>(
    null
  );
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [binLocation, setBinLocation] = useState("");
  const [toastShown, setToastShown] = useState(false);
  const location = useLocation();

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const [supplierActionSelections, setSupplierActionSelections] = useState<SelectionState>({});
  const [warehouseSelections, setWarehouseSelections] = useState<SelectionState>({});
  const [zoneSelections, setZoneSelections] = useState<SelectionState>({});
  const [rockSelections, setRockSelections] = useState<SelectionState>({});
  const [binLocationSelections, setBinLocationSelections] = useState<SelectionState>({});
  const [noteSelections, setNoteSelections] = useState<SelectionState>({});
  const [rotationProduct, setRotationProduct] = useState<SelectionState>({});
  const [warehouseData, setWarehouseData] = useState<Record<string, WarehouseHierarchy>>({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showBinLocationPopup, setShowBinLocationPopup] = useState<Record<string, boolean>>({});
  const [selectedLocations, setSelectedLocations] = useState<Record<string, Array<{
    quantity: number;
    warehouse_id: string; warehouse_name: string;
    zone_id: string; zone_name: string;
    rack_id: string; rack_name: string;
    bin_id: string; bin_name: string;
  }>>>({});
  const confirmedProductsRef = useRef<Set<string>>(new Set()); // Track products that were confirmed in current session

  const navigate = useNavigate();

  const itemsPerPage = 10;
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [dateFilter, setDateFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const [linkedStatusFilter, setLinkedStatusFilter] = useState("");
  const [currentStatusFilter, setCurrentStatusFilter] = useState("");
  const [tempLinkedStatus, setTempLinkedStatus] = useState(""); // UI dropdown
  const [tempCurrentStatus, setTempCurrentStatus] = useState(""); //
  const allImageIds = useMemo(() => {
    const ids: string[] = [];
    for (const row of (officeProducts || [])) {
      const id = (row?.product as any)?.images?.[0]?.url as string;
      if (isHexId(id)) ids.push(id.trim());
    }
    return Array.from(new Set(ids));
  }, [officeProducts]);

  // hook expects: [{ product: { variants: [{ images: [{ url }] }] } }]
  const dataForMap = useMemo(() => {
    if (allImageIds.length === 0) return [];
    return [
      { product: { variants: [{ images: allImageIds.map(url => ({ url })) }] } }
    ];
  }, [allImageIds]);

  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);


  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/incoming-office-products/datatable`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            search: globalFilter,
            // filters: {
            //   locked_status: linkedStatusFilter,
            //   current_status: currentStatusFilter,
            // },
            sort: {
              field: "created_at",
              order: "desc",
            },
          })
        }
      );
      const result = await response.json();
      // console.log("fetched Data :", result);
      setOfficeProducts(result?.data ?? []);
      setRowCount(result?.recordsTotal ?? 0);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/warehouses`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      // console.log("fetchWarehouses : ", data)
      setWarehouses(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchWarehouses();
  }, [token, globalFilter, linkedStatusFilter, currentStatusFilter, pagination]);

  //   if (loading) {
  //     return <div>Loading...</div>;
  //   }

  async function fetchHierarchy(payload: object) {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/warehouses/hierarchy`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to fetch hierarchy");
    return res.json();
  }

  const handleAcceptClick = (product: OfficeProduct) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!selectedProduct || !selectedWarehouse || !binLocation) {
      showError("Please fill in all fields.");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${selectedProduct?.office?._id}/inventory/${selectedProduct?.product_id}/office-decision`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            linked_mode: "accepted",
            warehouse_info: {
              id: selectedWarehouse,
              bin_location: binLocation,
            },
            action: "accept",
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to save location.");

      showSuccess("Location saved successfully!");
      setShowModal(false);
      navigate(0);
      fetchProducts();
    } catch (error) {
      showError("Failed to save location.");
      console.error(error);
    }
  };

  const handleSupplierActionChange = (productId: string, value: string) => {
    setSupplierActionSelections((prev) => ({
      ...prev,
      [productId]: value,
    }));
    // Automatically open popup when "with_supplier" is selected
    if (value === "with_supplier") {
      setShowBinLocationPopup(prev => ({ ...prev, [productId]: true }));
    }
  };

  const handleWarehouseChange = async (productId: string, warehouseId: string) => {
    setWarehouseSelections(prev => ({ ...prev, [productId]: warehouseId }));
    setZoneSelections(prev => ({ ...prev, [productId]: "" }));
    setRockSelections(prev => ({ ...prev, [productId]: "" }));
    setBinLocationSelections(prev => ({ ...prev, [productId]: "" }));
    if (warehouseId) {
      const result = await fetchHierarchy({ warehouse_id: warehouseId });
      // console.log("result", result);
      setWarehouseData(prev => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          zones: result.zones || [],
          racks: [],
          bins: [],
          selectedZone: undefined,
          selectedRack: undefined,
          selectedBin: undefined,
        }
      }));
    }
  };

  const handleZoneChange = async (productId: string, zoneId: string) => {
    setZoneSelections(prev => ({ ...prev, [productId]: zoneId }));
    setRockSelections(prev => ({ ...prev, [productId]: "" }));
    setBinLocationSelections(prev => ({ ...prev, [productId]: "" }));

    const zoneObj = warehouseData[productId]?.zones?.find(z => z._id === zoneId);
    if (zoneId) {
      const result = await fetchHierarchy({ zone_id: zoneId });
      setWarehouseData(prev => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          racks: result.racks || [],
          bins: [],
          selectedZone: zoneObj,
          selectedRack: undefined,
          selectedBin: undefined,
        }
      }));
    }
  };


  const handleRockChange = async (productId: string, rackId: string) => {
    setRockSelections(prev => ({ ...prev, [productId]: rackId }));
    setBinLocationSelections(prev => ({ ...prev, [productId]: "" }));

    const rackObj = warehouseData[productId]?.racks?.find(r => r._id === rackId);
    if (rackId) {
      const result = await fetchHierarchy({ rack_id: rackId });
      setWarehouseData(prev => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          bins: result.bins || [],
          selectedRack: rackObj,
          selectedBin: undefined,
        }
      }));
    }
  };


  const handleBinLocationChange = (productId: string, binId: string) => {
    setBinLocationSelections(prev => ({ ...prev, [productId]: binId }));
    const binObj = warehouseData[productId]?.bins?.find(b => b._id === binId);
    setWarehouseData(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        selectedBin: binObj
      }
    }));
  };

  const handleNoteChange = (productId: string, value: string) => {
    setNoteSelections((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleRotationalProduct = (productId: string, value: string) => {
    setRotationProduct((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleSupplierAction = async (productId: string, officeId: string) => {
    const action = supplierActionSelections[productId];
    // Find the product in your state, just like with agentProducts
    const officeProduct: OfficeProduct | undefined = officeProducts.find(p => p.product_id === productId);

    // Map action to specific endpoint suffix
    const actionEndpoints: Record<string, string> = {
      in_transit: "in-transit",
      on_site: "on-site",
      declined: "decline-after-pickup",
    };

    const endpointSuffix = actionEndpoints[action] || "accept";
    const endpoint = `${process.env.REACT_APP_API_URL}/linked-installer/office/${officeId}/product/${productId}/${endpointSuffix}`;

    const noteOrReason = noteSelections[productId] || "";
    const locations = selectedLocations[productId] || [];
    const totalQuantity = locations.reduce((sum, loc) => sum + (loc.quantity || 0), 0);

    // Pull quantity/location from variants/inventory
    const quantity = officeProduct?.product.variants?.[0]?.inventory?.[0]?.available || 0;
    const product_location_status = officeProduct?.product.product_location_status || "";
    const assigned_at = new Date().toISOString();

    // Determine what to send in the body
    const body =
      action === "declined"
        ? { reason: noteOrReason }
        : ["in_transit", "on_site"].includes(action)
          ? {
            note: noteOrReason,
            storage_locations: [
              {
                quantity: totalQuantity || quantity,
                product_location_status,
                current_status: action || "",
                location: [],
                assigned_at,
              }
            ]
          }
          : {
            storage_locations: [
              {
                quantity: totalQuantity || quantity,
                product_location_status,
                current_status: action || "",
                location: locations.map(loc => ({
                  quantity: loc.quantity,
                  warehouse_id: loc.warehouse_id,
                  warehouse_name: loc.warehouse_name,
                  zone_id: loc.zone_id,
                  zone_name: loc.zone_name,
                  rack_id: loc.rack_id,
                  rack_name: loc.rack_name,
                  bin_id: loc.bin_id,
                  bin_name: loc.bin_name,
                })),
                assigned_at,
              }
            ],
            is_rotational: false,
            note: noteOrReason,
          };

    try {
      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = data?.error || "Failed to updated product.";
        showError(errorMessage);
        alert(errorMessage);
        return;
      }
      showSuccess(data.message || "Status updated successfully");

      // Send notifications to signage team members, userId, and office team members when product link is requested/accepted
      if (action === "with_supplier" || endpointSuffix === "accept") {
        try {
          const { createNotification } = await import("../../services/notificationApi");
          const { getProductLinkRequestedSignageContent } = await import("../../services/notificationContent");
          const productName = officeProduct?.product?.name || "Product";
          
          // Get office name or brokerage firm name for the requester name
          const requesterName = officeProduct?.office?.officeName || officeProduct?.brokerage_firm || "Office User";
          
          // Get effective signage ID (parent if team member, otherwise userId)
          const effectiveSignageId = effectiveSignageOrgId;
          
          // 1. Send notification to userId (the signage user performing the action)
          if (userId && token) {
            try {
              const currentUserData = await fetchUserData(membershipId || userId, token);
              const userLicenseType = currentUserData?.licenseType || "SIGNAGE_TEAM_MEMBER";
              const userRecipientType = userLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
              const content = getProductLinkRequestedSignageContent({
                agentOrTeamMemberName: requesterName,
                productName: productName,
                recipientType: userRecipientType,
              });
              await createNotification(userId, content, token, userRecipientType);
            } catch (userErr) {
              console.error(`Failed to send notification to userId ${userId}:`, userErr);
            }
          }
          
          // 2. Send notifications to all signage team members
          if (effectiveSignageId && token) {
            try {
              const signageTeamResp = await fetch(
                `${process.env.REACT_APP_API_URL}/users/by-supplier/${effectiveSignageId}`,
                {
                  method: "GET",
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              );

              if (signageTeamResp.ok) {
                const signageTeamData = await signageTeamResp.json();
                const signageTeamMembers = Array.isArray(signageTeamData?.data) ? signageTeamData.data : [];

                // Send notifications to all signage admin/team members (excluding userId to avoid duplicate)
                for (const member of signageTeamMembers) {
                  const memberId = member.id || member._id;
                  if (memberId && memberId !== userId && token) {
                    try {
                      const memberLicenseType = member.licenseType || "SIGNAGE_TEAM_MEMBER";
                      const recipientType = memberLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
                      const content = getProductLinkRequestedSignageContent({
                        agentOrTeamMemberName: requesterName,
                        productName: productName,
                        recipientType: recipientType,
                      });
                      await createNotification(memberId, content, token, recipientType);
                    } catch (memberErr) {
                      console.error(`Failed to send notification to signage team member ${memberId}:`, memberErr);
                    }
                  }
                }
              }
            } catch (signageErr) {
              console.error("Failed to fetch signage team members for notification:", signageErr);
            }
          }
          
          // 3. Send notifications to office team members
          if (officeId && token) {
            try {
              const officeTeamMembers = await fetchOfficeTeamMembers(officeId, token);
              for (const teamMember of officeTeamMembers) {
                if (teamMember?._id && token) {
                  try {
                    const memberLicenseType = teamMember.licenseType || "BROKERAGE_TEAM_MEMBER";
                    const recipientType = memberLicenseType === "BROKERAGE_FIRM" ? "brokerage_admin" : "brokerage_team_member";
                    const content = getProductLinkRequestedSignageContent({
                      agentOrTeamMemberName: requesterName,
                      productName: productName,
                      recipientType: recipientType,
                    });
                    await createNotification(teamMember._id, content, token, recipientType);
                  } catch (memberErr) {
                    console.error(`Failed to send notification to office team member ${teamMember._id}:`, memberErr);
                  }
                }
              }
            } catch (officeErr) {
              console.error("Failed to fetch office team members for notification:", officeErr);
            }
          }
        } catch (notifError) {
          console.error("Failed to send product link requested notifications:", notifError);
        }
      }

      // Send email notifications
      try {
        // Determine trigger name based on action
        const triggerName = action === "declined" ? "Decline" : "Product link requested";
        
        // Get product details
        const productName = officeProduct?.product?.name || "";
        const sku = officeProduct?.product?.sku || "";
        const ownerNodeType = (officeProduct as any)?.owner_node_type || "";
        const ownerNodeId = (officeProduct as any)?.owner_node_id || "";
        const supplierName = userId ? await fetchUserData(membershipId || userId, token).then(u => u?.firstName || u?.email?.split("@")[0] || "Supplier") : "Supplier";
        const declinedBy = supplierName;
        const reason = noteOrReason || "No reason provided";
        const declinedOn = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
        const updatedOn = declinedOn;
        const newStatus = action === "declined" ? "Declined" : action === "in_transit" ? "In Transit" : action === "on_site" ? "On Site" : action === "with_supplier" ? "With Supplier" : "Accepted";
        const supplierComment = noteOrReason || "";

        // Collect recipient emails
        const recipientEmails: string[] = [];
        const licenseTypes: string[] = [];

        // Get customer group users by domain "warehouse"
        const effectiveSignageId = effectiveSignageOrgId;
        const customerGroup = await fetchCustomerGroupByDomain(organizationId, "warehouse", token);
        if (customerGroup?.allowed_user_ids) {
          customerGroup.allowed_user_ids.forEach((user: any) => {
            if (user?.email && user.email.trim()) {
              const email = user.email.trim();
              if (!recipientEmails.includes(email)) {
                recipientEmails.push(email);
                licenseTypes.push("SIGNAGE_TEAM_MEMBER"); // Assuming warehouse managers are team members
              }
            }
          });
        }

        // If owner_node_type is "brokerage", fetch and add brokerage email
        // if (ownerNodeType?.toLowerCase() === "brokerage" && ownerNodeId) {
        //   const brokerageUser = await fetchUserData(ownerNodeId, token);
        //   if (brokerageUser?.email) {
        //     const email = brokerageUser.email.trim();
        //     if (!recipientEmails.includes(email)) {
        //       recipientEmails.push(email);
        //       licenseTypes.push("BROKERAGE_FIRM");
        //     }
        //   }
        // }

        // Send emails if there are recipients
        if (recipientEmails.length > 0) {
          const from = process.env.REACT_APP_FROM_EMAIL2 || "";
          const emailParams: Record<string, string> = {
            productName: productName,
            sku: sku,
            supplierName: supplierName,
            declinedBy: declinedBy,
            reason: reason,
            declinedOn: declinedOn,
            updatedOn: updatedOn,
            newStatus: newStatus,
            supplierComment: supplierComment,
            firstName: "", // Will be replaced in email template
          };

          await sendTriggerEmails(
            triggerName,
            licenseTypes,
            emailParams,
            recipientEmails,
            from,
            token || ""
          );
          console.log(`Email notifications sent for ${triggerName}`);
        }
      } catch (emailError) {
        console.error("Failed to send email notifications:", emailError);
        // Don't fail the entire operation if email fails
      }

      navigate(0); // refreshes the page
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const handleUnlink = async (productId: string, officeId: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/linked-installer/office/${officeId}/product/${productId}/approve-unlink`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = data?.error || "Failed to UnLink product.";
        showError(errorMessage);
        alert(errorMessage);
        return;
      }
      showSuccess(data.message || "Product Unlinked successfully!");

      // Send notifications to signage team members, userId, and office team members when product is unlinked
      try {
        const { createNotification } = await import("../../services/notificationApi");
        const { getProductUnlinkedSignageContent } = await import("../../services/notificationContent");
        const officeProduct = officeProducts.find(
          p => p.product_id === productId && p.office._id === officeId
        );
        const productName = officeProduct?.product?.name || "Product";
        
        // Get office name or brokerage firm name for the requester name
        const requesterName = officeProduct?.office?.officeName || officeProduct?.brokerage_firm || "Office User";
        
        // Get effective signage ID (parent if team member, otherwise userId)
        const effectiveSignageId = effectiveSignageOrgId;
        
        // 1. Send notification to userId (the signage user performing the action)
        if (userId && token) {
          try {
            const currentUserData = await fetchUserData(membershipId || userId, token);
            const userLicenseType = currentUserData?.licenseType || "SIGNAGE_TEAM_MEMBER";
            const userRecipientType = userLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
            const content = getProductUnlinkedSignageContent({
              agentOrTeamMemberName: requesterName,
              productName: productName,
              recipientType: userRecipientType,
            });
            await createNotification(userId, content, token, userRecipientType);
          } catch (userErr) {
            console.error(`Failed to send notification to userId ${userId}:`, userErr);
          }
        }
        
        // 2. Send notifications to all signage team members
        if (effectiveSignageId && token) {
          try {
            const signageTeamResp = await fetch(
              `${process.env.REACT_APP_API_URL}/users/by-supplier/${effectiveSignageId}`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );

            if (signageTeamResp.ok) {
              const signageTeamData = await signageTeamResp.json();
              const signageTeamMembers = Array.isArray(signageTeamData?.data) ? signageTeamData.data : [];

              // Send notifications to all signage admin/team members (excluding userId to avoid duplicate)
              for (const member of signageTeamMembers) {
                const memberId = member.id || member._id;
                if (memberId && memberId !== userId && token) {
                  try {
                    const memberLicenseType = member.licenseType || "SIGNAGE_TEAM_MEMBER";
                    const recipientType = memberLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
                    const content = getProductUnlinkedSignageContent({
                      agentOrTeamMemberName: requesterName,
                      productName: productName,
                      recipientType: recipientType,
                    });
                    await createNotification(memberId, content, token, recipientType);
                  } catch (memberErr) {
                    console.error(`Failed to send notification to signage team member ${memberId}:`, memberErr);
                  }
                }
              }
            }
          } catch (signageErr) {
            console.error("Failed to fetch signage team members for notification:", signageErr);
          }
        }
        
        // 3. Send notifications to office team members
        if (officeId && token) {
          try {
            const officeTeamMembers = await fetchOfficeTeamMembers(officeId, token);
            for (const teamMember of officeTeamMembers) {
              if (teamMember?._id && token) {
                try {
                  const memberLicenseType = teamMember.licenseType || "BROKERAGE_TEAM_MEMBER";
                  const recipientType = memberLicenseType === "BROKERAGE_FIRM" ? "brokerage_admin" : "brokerage_team_member";
                  const content = getProductUnlinkedSignageContent({
                    agentOrTeamMemberName: requesterName,
                    productName: productName,
                    recipientType: recipientType,
                  });
                  await createNotification(teamMember._id, content, token, recipientType);
                } catch (memberErr) {
                  console.error(`Failed to send notification to office team member ${teamMember._id}:`, memberErr);
                }
              }
            }
          } catch (officeErr) {
            console.error("Failed to fetch office team members for notification:", officeErr);
          }
        }
      } catch (notifError) {
        console.error("Failed to send product unlinked notifications:", notifError);
      }

      navigate(0);
    } catch (error) {
      console.error("Error Unlink product link:", error);
    }
  };
  const columns = useMemo(() => {
    return [
      // S.No.
      // columnHelper.display({
      //   id: "serial",
      //   header: () => <span className="th-text S-No">S.No.</span>,
      //   cell: (info) => (
      //     <span className="cell-center S-No">
      //       {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
      //     </span>
      //   ),
      // }),

      // Image
      columnHelper.display({
        id: "image",
        header: () => <span className="th-text S-No">Image</span>,
        enableGlobalFilter: false,
        cell: (info) => {
          const raw = (info.row.original?.product as any)?.images?.[0]?.url as string | undefined;
          let url = "";

          if (raw) {
            if (ABS_URL.test(raw)) {
              // already a full URL
              url = raw;
            } else if (isHexId(raw)) {
              // hex id -> resolve via presigned map / helper
              const map: Record<string, string> = (imageUrlMap as any) || {};
              url = map[raw] || getImageSrc(raw, map) || "";
            } else {
              // legacy relative path
              const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
              const path = raw.replace(/^\/+/, "");
              url = `${base}/${encodeURI(path)}`;
            }
          }

          const src = url || imageCustomSquare;

          return (
            <div className="text-sm S-No">
              <img
                src={src}
                alt="Product"
                style={{ width: 50, height: 50, objectFit: "contain", borderRadius: 4 }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = imageCustomSquare;
                }}
              />
            </div>
          );

        },
      }),

      // Brokerage Firm (name aligned with Production Overview)
      columnHelper.accessor((row: OfficeProduct) => row?.brokerage_firm ?? "", {
        id: "brokerage_firm",
        header: () => <span className="th-text">Brokerage Firm</span>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: (info) => <span className="text-sm ">{info.getValue() || "-"}</span>,
      }),

      // Office
      columnHelper.accessor((row: OfficeProduct) => `${row?.office?.officeName}`, {
        id: "office",
        header: () => <span className="th-text">Office</span>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: (info) => <span className="text-sm">{info.getValue()}</span>,
      }),

      // Product Name
      columnHelper.accessor((row: OfficeProduct) => row?.product?.name, {
        id: "name",
        header: () => <span className="th-text">Product Name</span>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: (info) => <span className="text-sm wrap-anywhere">{info.getValue()}</span>,
      }),

      // SKU
      columnHelper.accessor((row: OfficeProduct) => row.product.sku || "-", {
        id: "sku",
        header: () => <span className="th-text">SKU</span>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: (info) => <span className="text-sm">{info.getValue()}</span>,
      }),

      // Category
      columnHelper.accessor((row: OfficeProduct) => row.product.category || "-", {
        id: "category",
        header: () => <span className="th-text">Category</span>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: (info) => <span className="text-sm">{info.getValue()}</span>,
      }),

      // Qty (center like Production table numbers)
      columnHelper.accessor(
        (row: OfficeProduct) => row.product.variants?.[0]?.inventory?.[0]?.available || 0,
        {
          id: "available",
          header: () => <span className="th-text">Qty</span>,
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
          cell: (info) => <span className="text-sm">{info.getValue()}</span>,
        }
      ),

      // Condition
      // columnHelper.accessor((row: OfficeProduct) => row.product.item_condition || "-", {
      //   id: "item_condition",
      //   header: () => <span className="th-text">Condition</span>,
      //   filterFn: fuzzyTextFilter,
      //   enableGlobalFilter: true,
      //   cell: (info) => <span className="text-sm">{info.getValue()}</span>,
      // }),

      // Handover Method
      columnHelper.accessor(
        (row: OfficeProduct) => row?.product?.product_location_status?.replace(/_/g, " "),
        {
          id: "product_location_status",
          header: () => <span className="th-text">Handover Method</span>,
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
          cell: (info) => <span className="text-sm">{info.getValue()}</span>,
        }
      ),

      // Product location status (badge component already matches)
      columnHelper.accessor(
        (row: OfficeProduct) => row?.product?.current_status?.replace(/_/g, " "),
        {
          id: "current_status",
          header: () => <span className="th-text">Product location status</span>,
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
          cell: (info) => (
            <div className="text-sm">
              <StatusBadge status={info.getValue()} />
            </div>
          ),
        }
      ),

      // Linked Status (small text)
      columnHelper.accessor(
        (row: OfficeProduct) => (row?.product?.locked_status === "unlocked" ? "" : "Linked"),
        {
          id: "linked_status",
          header: () => <span className="th-text">Linked Status</span>,
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
          cell: (info) => <span className="text-sm">{info.getValue() || "-"}</span>,
        }
      ),

      // Supplier Actions (styled like the Production pill selects)
      columnHelper.display({
        id: "supplierActions",
        header: () => <span className="th-text">Supplier Actions</span>,
        enableGlobalFilter: false,
        cell: ({ row, table }) => {
          const officeProduct = row.original;
          const m = table.options.meta as MyTableMeta;
          const locked = officeProduct?.product?.locked_status === "locked";
          const linkedMode = officeProduct?.product?.linked_mode || "";

          // If product is locked and not pending unlock, show "Already Linked" message
          if (locked && linkedMode !== "unlock_pending") {
            return (
              <div className="text-center d-flex justify-content-center align-items-center">
                <div
                  style={{
                    display: "inline-block",
                    padding: "3px 7px",
                    borderRadius: "0.525rem",
                    fontSize: "0.75rem",
                    textAlign: "center",
                    backgroundColor: "#d2f5d2",
                    color: "#368727",
                    border: "0.2px solid",
                    borderColor: "#36872750",
                    whiteSpace: "nowrap",
                  }}
                >
                  Already Linked
                </div>
              </div>
            );
          }

          if (linkedMode === "unlock_pending") {
            return (
              <div className="text-sm">
                <p className="text-danger mb-1 fw-bold">Unlink Requested</p>
                <button
                  className="btn btn-danger btn-sm mb-2"
                  onClick={() => m.handleUnlink(officeProduct?.product_id, officeProduct?.office._id)}
                >
                  Approve
                </button>
              </div>
            );
          }

          const currentStatus = officeProduct?.product?.current_status || "";
          const supplierActionValue = m.supplierActionSelections[officeProduct.product_id] ?? "";
          const productId = officeProduct.product_id;

          // Confirm button enable logic
          const canConfirmSimple =
            (currentStatus === "awaiting_response" || currentStatus === "in_transit") &&
            supplierActionValue !== "";

          const canConfirmPendingDropoff =
            currentStatus === "pending_dropoff" &&
            (supplierActionValue === "declined" ||
              (supplierActionValue === "with_supplier" &&
                m.selectedLocations[productId] &&
                m.selectedLocations[productId].length > 0));

          const confirmDisabled = locked || !(canConfirmSimple || canConfirmPendingDropoff);

          return (
            <div className="cell supplier-actions-grid ">
              {/* main select */}
              <select
                className="form-select po-select w-100 mb-2 supplier-select"
                value={supplierActionValue}
                onChange={(e) =>
                  m.handleSupplierActionChange(officeProduct.product_id, e.target.value)
                }
              >
                {currentStatus === "awaiting_response" ? (
                  <>
                    <option value="" disabled>Select</option>
                    <option value="in_transit">In Transit</option>
                    <option value="declined">Decline</option>
                  </>
                ) : currentStatus === "in_transit" ? (
                  <>
                    <option value="" disabled>Select</option>
                    <option value="with_supplier">With Supplier</option>
                    <option value="on_site">On site</option>
                    <option value="declined">Decline</option>
                  </>
                ) : currentStatus === "pending_dropoff" ? (
                  <>
                    <option value="" disabled>Select</option>
                    <option value="with_supplier">With Supplier</option>
                    <option value="declined">Decline</option>
                  </>
                ) : (
                  <option value="">Select</option>
                )}
              </select>

              {/* conditional selects */}
              {supplierActionValue === "with_supplier" && (
                <>
                  {/* Popup opens automatically when "with_supplier" is selected */}
                </>
              )}

              {/* notes + confirm */}
              <textarea
                className="form-control po-notes w-100"
                placeholder={
                  ["declined", "reject"].includes(supplierActionValue)
                    ? "Add Reason..." : "Add notes..."
                }
                value={m.noteSelections[productId] ?? ""}
                onChange={(e) => m.handleNoteChange(productId, e.target.value)}
              />
              <div className="d-grid d-sm-block text-center mt-2">
                <button
                  className="export-button mt-2 mt-sm-0 confirm-btn"
                  onClick={() =>
                    m.handleSupplierAction(officeProduct?.product_id, officeProduct?.office._id)
                  }
                  disabled={confirmDisabled}
                  style={{ pointerEvents: confirmDisabled ? 'none' : 'auto' }}
                >
                  Confirm
                </button>
              </div>
            </div>
          );

        },
      }),

      // Actions (eye icon pill)
      columnHelper.display({
        id: "actions",
        header: () => <span className="th-text">Actions</span>,
        cell: (info) => {
          const p = info.row.original;
          if (!canView && !canEdit && !canDelete) return null;
          return (
            <div className="text-sm ">
              {canView && (
                <ViewIconButton
                  to={`${baseRoute}/viewOfficeProduct/${p?.office._id}/${p?.product_id}`}
                  className="btn action-btn fs-2 text-center"
                  aria-label="View"
                  title="View"
                />
              )}
            </div>
          );
        },
      }),
    ];
  }, [pagination.pageIndex, pagination.pageSize,imageUrlMap, canView, canEdit, canDelete, baseRoute]);

  // const columns = useMemo(() => {
  //   return [
  //     columnHelper.display({
  //       id: "serial",
  //       header: () => (
  //         <div className="text-center w-100 table-headers">S.No.</div>
  //       ),
  //       cell: (info) => (
  //         <div className="text-center w-100 table-cells">
  //           {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
  //         </div>
  //       ),
  //     }),
  //     columnHelper.display({
  //       id: "image",
  //       header: () => (
  //         <div className="text-center w-100 table-headers">Image</div>
  //       ),
  //       cell: (info) => {
  //         const url = info.row.original?.product?.images?.length > 0
  //           ? `${process.env.REACT_APP_BASE_URL}${info.row.original.product.images[0].url}`
  //           : "";
  //         return (
  //           <div className="text-center w-100 table-cells">
  //             {url ? (
  //               <img src={url} alt="Product" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4 }} />
  //             ) : (
  //               <span>No Image</span>
  //             )}
  //           </div>
  //         );
  //       },
  //       enableGlobalFilter: false,
  //     }),
  //     columnHelper.accessor(
  //       (row: OfficeProduct) => {
  //         // console.log("brokerage_firm", row?.brokerage_firm);
  //         return row?.brokerage_firm ?? "";
  //       },
  //       {
  //         id: "Brokerage Name",
  //         header: () => <div className="table-headers text-center">Brokerage Name</div>,
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //         cell: (info) => (
  //           <span className="table-cells d-flex justify-content-center">
  //             {info.getValue() || "-"}
  //           </span>
  //         ),
  //       }
  //     ),
  //   // columnHelper.accessor(
  //   //   (row: OfficeProduct) => `${row?.brokerage_firm}`,
  //   //   {
  //   //     id: "Brokerage Name",
  //   //     header: () => <div className="table-headers text-center">Brokerage Name</div>,
  //   //     filterFn: fuzzyTextFilter,
  //   //     enableGlobalFilter: true,
  //   //     cell: info => <span className="table-cells d-flex justify-content-center">{"-"}</span>,
  //   //   }),
  //   columnHelper.accessor(
  //     (row: OfficeProduct) => `${row?.office?.officeName}`,
  //     {
  //       id: "office",
  //       header: () => <div className="table-headers text-center">Office</div>,
  //       filterFn: fuzzyTextFilter,
  //       enableGlobalFilter: true,
  //       cell: info => <span className="table-cells text-center">{info.getValue()}</span>,
  //     }),
  //     columnHelper.accessor(
  //       (row: OfficeProduct) => row?.product?.name,
  //       {
  //         id: "name",
  //         header: () => (
  //           <div className="text-center w-100 table-headers">Product Name</div>
  //         ),
  //         cell: (info) => (
  //           <div className="text-center w-100 table-cells text-ellipsis">{info.getValue()}</div>
  //         ),
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }
  //     ),
  //     columnHelper.accessor(
  //       (row: OfficeProduct) => row.product.sku || "-",
  //       {
  //         id: "sku",
  //         header: () => (
  //           <div className="text-center w-100 table-headers">SKU</div>
  //         ),
  //         cell: (info) => (
  //           <div className="text-center w-100 table-cells">{info.getValue()}</div>
  //         ),
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }
  //     ),
  //     columnHelper.accessor(
  //       (row: OfficeProduct) => row.product.category || "-",
  //       {
  //         id: "category",
  //         header: () => (
  //           <div className="text-center w-100 table-headers">Category</div>
  //         ),
  //         cell: (info) => (
  //           <div className="text-center w-100 table-cells text-ellipsis">{info.getValue()}</div>
  //         ),
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }
  //     ),
  //     columnHelper.accessor(
  //       (row: OfficeProduct) => row.product.variants?.[0]?.inventory?.[0]?.available || 0,
  //       {
  //         id: "available",
  //         header: () => (
  //           <div className="text-center w-100 table-headers">Qty</div>
  //         ),
  //         cell: (info) => (
  //           <div className="text-center w-100 table-cells">{info.getValue()}</div>
  //         ),
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }
  //     ),
  //     columnHelper.accessor(
  //       (row: OfficeProduct) => row.product.item_condition || "-",
  //       {
  //         id: "item_condition",
  //         header: () => (
  //           <div className="text-center w-100 table-headers">Condition</div>
  //         ),
  //         cell: (info) => (
  //           <div className="text-center w-100 table-cells">{info.getValue()}</div>
  //         ),
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }
  //     ),
  //     // columnHelper.accessor(
  //     //   (row: OfficeProduct) => row.created_by?.name || "-",
  //     //   {
  //     //     id: "created_by",
  //     //     header: () => (
  //     //       <div className="text-center w-100 table-headers">Linked By</div>
  //     //     ),
  //     //     cell: (info) => (
  //     //       <div className="text-center w-100 table-cells">{info.getValue()}</div>
  //     //     ),
  //     //     filterFn: fuzzyTextFilter,
  //     //     enableGlobalFilter: true,
  //     //   }
  //     // ),
  //     columnHelper.accessor(
  //       (row: OfficeProduct) => row?.product?.product_location_status?.replace(/_/g, " "),
  //       {
  //         id: "product_location_status",
  //         header: () => (
  //           <div className="text-center w-100 table-headers">Handover Method</div>
  //         ),
  //         cell: (info) => (
  //           <div className="text-center w-100 table-cells">{info.getValue()}</div>
  //         ),
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }
  //     ),
  //     columnHelper.accessor(
  //       (row: OfficeProduct) => row?.product?.current_status?.replace(/_/g, " "),
  //       {
  //         id: "current_status",
  //         header: () => (
  //           <div className="text-center w-100 table-headers">Product location status</div>
  //         ),
  //         cell: (info) => (
  //           <div className="text-center table-cells">
  //             <StatusBadge status={info.getValue()} />
  //           </div>
  //         ),
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }
  //     ),
  //     columnHelper.accessor(
  //       (row: OfficeProduct) => row?.product?.locked_status === "unlocked" ? "" : "Linked",
  //       {
  //         id: "linked_status",
  //         header: () => (
  //           <div className="text-center w-100 table-headers">Linked Status</div>
  //         ),
  //         cell: (info) => (
  //           <div className="text-center w-100 table-cells linked-status">{info.getValue() || "-"}</div>
  //         ),
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }
  //     ),
  //     columnHelper.display({
  //       id: "supplierActions",
  //       header: () => (
  //         <div className="text-center w-100 table-headers">Supplier Actions</div>
  //       ),
  //       cell: ({ row, table }) => {
  //         const officeProduct = row.original;
  //         const m = table.options.meta as MyTableMeta;
  //         return (
  //           <div className="text-center w-100 table-cells">
  //             {officeProduct?.product?.linked_mode === "unlock_pending" ? (
  //               <>
  //                 <p className="text-danger mb-1 fw-bold">
  //                   Unlink Requested
  //                 </p>
  //                 <button
  //                   className="btn btn-danger btn-sm mb-2"
  //                   onClick={() =>
  //                     m.handleUnlink(officeProduct?.product_id, officeProduct?.office._id)
  //                   }
  //                 >
  //                   Approve
  //                 </button>
  //               </>
  //             ) : (
  //               <select
  //                 className="form-control"
  //                 value={m.supplierActionSelections[officeProduct.product_id] ?? ""}
  //                 onChange={(e) => m.handleSupplierActionChange(officeProduct.product_id, e.target.value)}
  //                 disabled={officeProduct?.product?.locked_status === "locked"}
  //               >
  //                 {officeProduct?.product?.current_status === "awaiting_response" ? (
  //                   <>
  //                     <option value="" disabled>Select</option>
  //                     <option value="in_transit">In Transit</option>
  //                     <option value="declined">Decline</option>
  //                   </>
  //                 ) : officeProduct?.product?.current_status === "in_transit" ? (
  //                   <>
  //                     <option value="" disabled>Select</option>
  //                     <option value="with_supplier">With Supplier</option>
  //                     <option value="on_site">On site</option>
  //                     <option value="declined">Decline</option>
  //                   </>
  //                 ) : officeProduct?.product?.current_status === "pending_dropoff" ? (
  //                   <>
  //                     <option value="" disabled>Select</option>
  //                     <option value="with_supplier">With Supplier</option>
  //                     <option value="declined">Decline</option>
  //                   </>
  //                 ) : (
  //                   <></>
  //                 )}
  //               </select>
  //             )}

  //             {m.supplierActionSelections[officeProduct.product_id] === "with_supplier" && (
  //               <>
  //                 <select
  //                   className="form-control mb-2"
  //                   value={m.warehouseSelections[officeProduct.product_id] ?? ""}
  //                   onChange={async (e) => { await m.handleWarehouseChange(officeProduct.product_id, e.target.value) }}
  //                 >
  //                   <option value="">Select Warehouse</option>
  //                   {m.warehouses?.map((wh) => (
  //                     <option key={wh._id} value={wh._id}>{wh.label}</option>
  //                   ))}
  //                 </select>
  //                 {/* Zone Dropdown */}
  //                 <select
  //                   className="form-control mb-2"
  //                   value={m.zoneSelections[officeProduct.product_id] ?? ""}
  //                   onChange={async (e) => {
  //                     await m.handleZoneChange(officeProduct.product_id, e.target.value);
  //                   }}
  //                   disabled={!m.warehouseSelections[officeProduct.product_id]}
  //                 >
  //                   <option value="">Select Zone</option>
  //                   {(m.warehouseData[officeProduct.product_id]?.zones || []).map(zone => (
  //                     <option key={zone._id} value={zone._id}>{zone.label}</option>
  //                   ))}
  //                 </select>
  //                 {/* Rack Dropdown */}
  //                 <select
  //                   className="form-control mb-2"
  //                   value={m.rockSelections[officeProduct.product_id] ?? ""}
  //                   onChange={async (e) => {
  //                     await m.handleRockChange(officeProduct.product_id, e.target.value);
  //                   }}
  //                   disabled={!m.zoneSelections[officeProduct.product_id]}
  //                 >
  //                   <option value="">Select Rack</option>
  //                   {(m.warehouseData[officeProduct.product_id]?.racks || []).map(rack => (
  //                     <option key={rack._id} value={rack._id}>{rack.label}</option>
  //                   ))}
  //                 </select>
  //                 {/* Bin Dropdown */}
  //                 <select
  //                   className="form-control mb-2"
  //                   value={m.binLocationSelections[officeProduct.product_id] ?? ""}
  //                   onChange={(e) => m.handleBinLocationChange(officeProduct.product_id, e.target.value)}
  //                   disabled={!m.rockSelections[officeProduct.product_id]}
  //                 >
  //                   <option value="">Select Bin</option>
  //                   {(m.warehouseData[officeProduct.product_id]?.bins || []).map(bin => (
  //                     <option key={bin._id} value={bin._id}>{bin.label}</option>
  //                   ))}
  //                 </select>
  //                 {/* Rotational Product Dropdown */}
  //                 <select
  //                   className="form-control mb-2"
  //                   value={m.rotationProduct[officeProduct.product_id] ?? ""}
  //                   onChange={(e) => m.handleRotationalProduct(officeProduct.product_id, e.target.value)}
  //                 >
  //                   <option value="">Is Rotational Product?</option>
  //                   <option value="Yes">Yes</option>
  //                   <option value="No">No</option>
  //                 </select>
  //               </>
  //             )}

  //             <textarea
  //               className="form-control"
  //               placeholder={
  //                 ["declined", "reject"].includes(m.supplierActionSelections[officeProduct.product_id])
  //                   ? "Add Reason..." : "Add notes..."
  //               }
  //               value={m.noteSelections[officeProduct.product_id] ?? ""}
  //               onChange={(e) => m.handleNoteChange(officeProduct.product_id, e.target.value)}
  //               disabled={
  //                 officeProduct?.product?.locked_status === "locked"
  //                 && officeProduct?.product?.linked_mode !== "unlock_pending"
  //               }
  //             />
  //             <button
  //               className="accept-btn btn btn-success mt-2"
  //               onClick={() =>
  //                 m.handleSupplierAction(officeProduct?.product_id, officeProduct?.office._id)
  //               }
  //               disabled={officeProduct?.product?.locked_status === "locked"}
  //             >
  //               Confirm
  //             </button>
  //           </div>
  //         );
  //       },
  //       enableGlobalFilter: false,
  //     }),
  //     columnHelper.display({
  //       id: "actions",
  //       header: () => (
  //         <div className="text-center w-100 table-headers">Actions</div>
  //       ),
  //       cell: (info) => {
  //         const productInfo = info.row.original;
  //         return (
  //           <div className="w-100 table-cells d-flex justify-content-center align-items-center gap-2">
  //             <Link
  //               to={`${baseRoute}/viewOfficeProduct/${productInfo?.office._id}/${productInfo?.product_id}`}
  //             >
  //               <i className="ri-eye-fill text-primary"></i>
  //             </Link>
  //             {/* <Link
  //             to={`${baseRoute}/editIncomingProduct/${officeProduct?.office._id}/${officeProduct?.product_id}`}
  //             className="btn btn-warning btn-sm"
  //           >
  //             Edit
  //           </Link> */}
  //           </div>
  //         );
  //       },
  //     }),
  //   ];
  // }, [pagination.pageIndex, pagination.pageSize]);


  const table = useReactTable<OfficeProduct>({
    data: officeProducts || [],
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      noteSelections,
      binLocationSelections,
      rotationProduct,
      warehouseSelections,
      supplierActionSelections,
      handleNoteChange,
      handleBinLocationChange,
      handleRotationalProduct,
      handleWarehouseChange,
      handleSupplierActionChange,
      handleSupplierAction,
      handleUnlink,
      warehouses,
      zoneSelections,
      rockSelections,
      handleZoneChange,
      handleRockChange,
      warehouseData,
      showBinLocationPopup,
      setShowBinLocationPopup,
      selectedLocations,
    },
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <div className="content">
      <div className="po-header mb-4 shadow-border-datatables">
        {/* TOP ROW */}
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
            <h4 className="mb-1 po-title">Incoming Office Products</h4>
            <div className="po-subtitle">Manage incoming products from suppliers and brokerages</div>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Product Name, SKU, Brokerages…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setGlobalFilter(searchInput); }}
                autoComplete="off"
              />
            </div>

            {/* Filters toggle */}
            <button
              type="button"
              className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen(v => !v)}
              aria-expanded={filtersOpen}
              aria-controls="incoming-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>

            {/* Export */}
            <button className="export-button align-items-center">
              <i className="ri-download-2-line me-2" />
              Export to Excel
            </button>
          </div>
        </div>

        <div id="incoming-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
          <div className="po-filters-bar d-flex"> {/* <-- flex wrapper */}
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">
              <div className="col-12 col-md-auto">
                <label htmlFor="linkedStatusFilter" className="form-label mb-1 label-1">Linked Status</label>
                <select
                  id="linkedStatusFilter"
                  className="form-select"
                  value={tempLinkedStatus}
                  onChange={(e) => setTempLinkedStatus(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="accepted">Linked</option>
                  <option value="unlock_pending">Unlinked</option>
                </select>
              </div>

              <div className="col-12 col-md-auto">
                <label htmlFor="currentStatusFilter" className="form-label mb-1 label-1">Current Status</label>
                <select
                  id="currentStatusFilter"
                  className="form-select"
                  value={tempCurrentStatus}
                  onChange={(e) => setTempCurrentStatus(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="With Supplier">With Supplier</option>
                  <option value="In Warehouse">In Warehouse</option>
                </select>
              </div>

              <div className="col-12 col-md-auto d-flex justify-content-md-end gap-2 mt-1 mt-md-0">
                <button
                  className="btn btn-primary table-addbutton flex-fill flex-md-grow-0"
                  onClick={() => {
                    setLinkedStatusFilter(tempLinkedStatus);
                    setCurrentStatusFilter(tempCurrentStatus);
                  }}
                >
                  <i className="ri-check-line me-1" />
                  Apply Filter
                </button>

                <button
                  className="btn back-btn surface-btn fw-bold fs-16 flex-fill flex-md-grow-0"
                  onClick={() => {
                    setTempLinkedStatus("all");
                    setTempCurrentStatus("all");
                    setLinkedStatusFilter("all");
                    setCurrentStatusFilter("all");
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>


        {/* COLLAPSIBLE FILTER BAR */}
        {/* <div id="incoming-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
    <div className="po-filters-bar">
      <div className="row g-2 g-md-3 align-items-end">
        <div className="col-12 col-md-2">
          <label htmlFor="linkedStatusFilter" className="form-label mb-1">Linked Status</label>
          <select
            id="linkedStatusFilter"
            className="form-select"
            value={tempLinkedStatus}
            onChange={(e) => setTempLinkedStatus(e.target.value)}
          >
            <option value="all">All</option>
            <option value="accepted">Linked</option>
            <option value="unlock_pending">Unlinked</option>
          </select>
        </div>

        <div className="col-12 col-md-2">
          <label htmlFor="currentStatusFilter" className="form-label mb-1">Current Status</label>
          <select
            id="currentStatusFilter"
            className="form-select"
            value={tempCurrentStatus}
            onChange={(e) => setTempCurrentStatus(e.target.value)}
          >
            <option value="all">All</option>
            <option value="With Supplier">With Supplier</option>
            <option value="In Warehouse">In Warehouse</option>
          </select>
        </div>

        <div className="col-12 col-md-4 d-flex justify-content-md-end gap-2">
          <button
            className="btn btn-primary flex-fill flex-md-grow-0"
            onClick={() => {
              setLinkedStatusFilter(tempLinkedStatus);
              setCurrentStatusFilter(tempCurrentStatus);
            }}
          >
            <i className="ri-check-line me-1" />
            Apply Filter
          </button>

          <button
            className="btn btn-light border flex-fill flex-md-grow-0"
            onClick={() => {
              setTempLinkedStatus("all");
              setTempCurrentStatus("all");
              setLinkedStatusFilter("all");
              setCurrentStatusFilter("all");
            }}
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  </div> */}
      </div>
      {/* <div className="d-flex  justify-content-between align-items-center border-bottom pb-3 mb-4 page-container">
      <h4 className="mb-0 fw-bold">Incoming Office Products</h4>

      <div className="d-flex gap-3 align-items-center">
        <div className="input-group" style={{ width: "230px", height: "40px" }}>
          <span className="input-group-text bg-white border-end-0 p-2 left-radius">
            <i className="ri-search-line fs-15"></i>
          </span>
          <input
            type="text"
            id="searchInput"
            name="searchInput"
            className="form-control border-start-0 ps-0 right-radius text-ellipsis"
            placeholder="Search by Product Name,SKU etc.."
            value={searchInput}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                setGlobalFilter(searchInput);
              }
            }}
            style={{ borderColor: "#cccccc" }}
          />

        </div>
        <button className="btn btn-outline-dark d-flex align-items-center">
                        <i className="ri-download-2-line me-1"></i> Import & Export
                      </button>
      </div>
    </div>
    <div className="row mb-3">
      <div className="col-md-4 d-flex gap-2" >
        <select
          name="linkedStatusFilter"
          id="selectionincomingagentproduct"

          className="form-select"
          value={tempLinkedStatus}
          onChange={(e) => setTempLinkedStatus(e.target.value)}
        >            <option value="all">Linked Status</option>
          <option value="accepted">Linked</option>
          <option value="unlock_pending">Unlinked</option>
        </select>

        <select
          name="currentStatusFilter"
          className="form-select"
          id="selectionincomingagentproduct"

          value={tempCurrentStatus}
          onChange={(e) => setTempCurrentStatus(e.target.value)}
        >            <option value="all">Current Status</option>
          <option value="With Supplier">With Supplier</option>
          <option value="In Warehouse">In Warehouse</option>
        </select>
        <button
          className="btn btn-primary w-25"
          onClick={() => {
            setLinkedStatusFilter(tempLinkedStatus);
            setCurrentStatusFilter(tempCurrentStatus);
          }}
        >
          Apply Filter
        </button>
      </div>

    </div> */}

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <div className="d-flex align-items-center">
            <i
              className="bi bi-box-seam me-2"
              aria-hidden="true"
              style={{ fontSize: 18, color: "#2563eb", lineHeight: 1 }}
            />
            <h5 className="mb-0">Incoming Products</h5>
          </div>

          {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
            orders
          </span> */}
        </div>


        <hr className="mb-2" style={{ borderTop: "none" }} />


        {/* DESKTOP/TABLET */}
        <div className="rdt-desktop ">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              {loading ? (
                <DesktopTableLoader table={table} message="Loading incoming office products…" />
              ) : (
                <DesktopTableParts table={table} />
              )}
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile ">
          {loading ? (
            <MobileCardsLoader message="Loading incoming office products…" />
          ) : (
            <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
          />
          )}
        </div>

      </div>



      {/* Table  */}
      {/* <DataTable table={table} /> */}

      {/* Pagination */}
      <DataTableStatus
        loading={loading}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>

      {/* Bin Location Popups */}
      {(officeProducts || []).map((product) => (
        <BinLocationPopUp
          key={product.product_id}
          show={showBinLocationPopup[product.product_id] || false}
          onHide={() => {
            setShowBinLocationPopup(prev => ({ ...prev, [product.product_id]: false }));
            // If user closed popup without confirming (clicking Save), clear "With Supplier" selection
            if (!confirmedProductsRef.current.has(product.product_id)) {
              setSupplierActionSelections(prev => ({ ...prev, [product.product_id]: "" }));
            }
            // Reset the confirmed flag for this product when popup closes
            confirmedProductsRef.current.delete(product.product_id);
          }}
          onConfirm={(locations, totalQty) => {
            setSelectedLocations(prev => ({ ...prev, [product.product_id]: locations }));
            // Mark this product as confirmed in this session
            confirmedProductsRef.current.add(product.product_id);
            // Keep "With Supplier" selected if any quantity is allocated and saved
            if (locations && locations.length > 0 && totalQty > 0) {
              // Keep "With Supplier" selected - user has allocated and saved
            } else {
              // Clear "With Supplier" if no locations or no quantity allocated
              setSupplierActionSelections(prev => ({ ...prev, [product.product_id]: "" }));
            }
          }}
          productQuantity={product?.product.variants?.[0]?.inventory?.[0]?.available || 0}
          ownerNodeId={effectiveSignageOrgId || organizationId || ""}
        />
      ))}

      {/* Accept Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Accept Product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Select Warehouse</Form.Label>
              <Form.Control
                as="select"
                value={selectedWarehouse}
                onChange={(e) => setSelectedWarehouse(e.target.value)}
              >
                <option value="">Select a warehouse</option>
                {warehouses?.map((wh) => (
                  <option key={wh._id} value={wh._id}>
                    {wh.label}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Bin Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter bin location"
                value={binLocation}
                onChange={(e) => setBinLocation(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </div>

  );
};

export default SignageIncomingOfficeProducts;
